import { Market, MarketID, MarketState, MarketConfig, FundingEvent } from '@/api/spec';
import { Exchange } from '../../../types/Exchange.ts';
import { MarketData } from '@/common/MarketData.ts';
import { parseMarketBoost } from '@/common/boostedMarkets';
import { FUNDING_INTERVAL_BLOCKS, FUNDING_INTERVAL_SEC } from '@/common/timeout_constants.ts';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils.ts';
import { formatNumberDisplay } from '@/utils/bigUtils';
import { leverageFromHundredths } from '../../trading/utils/leverageUtils.ts';

const formatUsdValue = (value: number | undefined | string): string => {
  if (value == null || value === '') return '';
  return `$${formatNumberDisplay(value, { minDecimals: 2, maxDecimals: 2 })}`;
};

const DEFAULT_PRICE_DECIMALS = 2;
const DEFAULT_TOKEN_DECIMALS = 6;
const DEFAULT_MAINTENANCE_MARGIN = 20;
const DEFAULT_INITIAL_MARGIN = 1;

const convertScaledPrice = (scaledValue: number, decimals: number): number | undefined => {
  if (scaledValue == null) return undefined;
  return offsetNumberToNumber(scaledValue, decimals);
};

const convertTokenAmount = (amount: string, tokenDecimals: number): number | undefined => {
  if (amount == null) return undefined;
  return parseFloat(amount) / Math.pow(10, tokenDecimals);
};

const calculate24hChange = (
  currentPrice: number,
  historicalPrice: number,
  priceDecimals: number
): { changeValue: number; changePercentage: number } => {
  if (historicalPrice === 0) {
    return { changeValue: 0, changePercentage: 0 };
  }

  const changeValue = currentPrice - historicalPrice;
  const changePercentage = (changeValue / historicalPrice) * 100;

  return {
    changeValue: parseFloat(changeValue.toFixed(priceDecimals)),
    changePercentage: parseFloat(changePercentage.toFixed(2)),
  };
};

const convertFeeToBigInt = (fee: number): bigint | undefined => {
  return fee != null ? BigInt(fee) : undefined;
};

const calculateNextFundingBlock = (
  currentBlock: number,
  lastFundingBlock: number,
  fundingIntervalBlocks: number | undefined
): number => {
  const intervalBlocks = fundingIntervalBlocks || FUNDING_INTERVAL_BLOCKS;

  // Calculate how many intervals have passed since the last funding event
  // If currentBlock < lastFundingBlock (shouldn't happen normally), max with 0
  const intervalsPassed = Math.max(0, Math.floor((currentBlock - lastFundingBlock) / intervalBlocks));

  return lastFundingBlock + (intervalsPassed + 1) * intervalBlocks;
};

function mapMarketData(market: Market, exchange: Exchange, isFavoriteMarket?: boolean): MarketData {
  const { state, config, funding, funding_interval_sec, funding_interval_blocks } = market;

  const priceDecimals = config.price_decimals ?? DEFAULT_PRICE_DECIMALS;
  const quoteDecimals = exchange.token.decimals ?? DEFAULT_PRICE_DECIMALS;
  const assetDecimals = config.size_decimals ?? DEFAULT_TOKEN_DECIMALS;

  const markPrice = convertScaledPrice(state.mrk, priceDecimals) ?? 0;
  const lastPrice = convertScaledPrice(state.lst, priceDecimals) ?? 0;
  const oraclePrice = convertScaledPrice(state.orl, priceDecimals) ?? 0;
  const historicalPrice = convertScaledPrice(state.prv, priceDecimals) ?? 0;

  const change24h = calculate24hChange(lastPrice, historicalPrice, priceDecimals);

  const volumeValue = convertTokenAmount(state.dva, quoteDecimals);
  const openInterestSizeValue = convertScaledPrice(state.oi, assetDecimals) ?? 0;
  const openInterestValue = openInterestSizeValue * oraclePrice;

  const fundingData = mapFundingData(funding, priceDecimals, state.at.b ?? 0, funding_interval_blocks);

  return {
    priceDirection: 'unchanged',
    id: market.id,
    instanceId: market.instance_id,
    name: market.name,
    symbol: market.size_units,
    assetDecimals: assetDecimals,
    quote: exchange.token,
    quoteDecimals: priceDecimals,
    markPrice: markPrice,
    oraclePrice: oraclePrice,
    lastPrice: lastPrice,
    midPrice: convertScaledPrice(state.mid, priceDecimals),
    bestBidPrice: convertScaledPrice(state.bid, priceDecimals),
    bestAskPrice: convertScaledPrice(state.ask, priceDecimals),
    change24hValue: change24h.changeValue,
    change24hPercentage: change24h.changePercentage,
    fundingRateCountdownMinutes: (funding_interval_sec || FUNDING_INTERVAL_SEC) / 60,
    volume: formatUsdValue(volumeValue),
    volumeValue,
    volumeSize: convertScaledPrice(state.dv, assetDecimals)?.toLocaleString() ?? '',
    openInterest: formatUsdValue(openInterestValue),
    openInterestValue,
    openInterestSize: openInterestSizeValue.toLocaleString(),
    openInterestSizeValue,
    totalCollateralLocked: formatNumberDisplay(convertTokenAmount(state.tvl, quoteDecimals), {
      maxDecimals: priceDecimals,
    }),
    isOpen: config.is_open,
    maintenanceMarginFraction: leverageFromHundredths(config.maintenance_margin) || DEFAULT_MAINTENANCE_MARGIN,
    initialMarginFraction: leverageFromHundredths(config.initial_margin) || DEFAULT_INITIAL_MARGIN,
    category: [],
    isFavorite: isFavoriteMarket ?? false,
    makerFeeMicros: convertFeeToBigInt(config.maker_fee),
    takerFeeMicros: convertFeeToBigInt(config.taker_fee),
    makerFeesMicros: config.maker_fees?.map((fee) => BigInt(fee)),
    takerFeesMicros: config.taker_fees?.map((fee) => BigInt(fee)),
    contractVersion: config.contract_version,
    minPostingAmount: convertTokenAmount(config.min_posting_amount, assetDecimals),
    minSettleAmount: convertTokenAmount(config.min_settle_amount, assetDecimals),
    recycleFee: convertTokenAmount(config.recycle_fee, assetDecimals),
    orderRetryBlocks: market.order_retry_blocks,
    orderTtlBlocks: market.order_ttl_blocks,
    orderMaxImpactP: market.order_max_market_slippage_bps / 100,
    perpId: market.perpetual_id,
    fundingIntervalBlocks: funding_interval_blocks,
    stateTimestamp: state.at,
    configTimestamp: config.at,
    version: market.ver,
    boost: parseMarketBoost(market.boost_params),
    ...fundingData,
  };
}

export const mapFundingData = (
  funding: FundingEvent,
  priceDecimals: number,
  currentBlock: number,
  fundingIntervalBlocks?: number
) => {
  return {
    fundingRateValue: funding.rate, // Already in 10^-6 fractions as per spec
    fundingIndexPrice: convertScaledPrice(funding.idx, priceDecimals),
    fundingSum: convertScaledPrice(funding.sum, priceDecimals),
    // Sourced per-event (not from config) so it can't drift from the `sum` it scales
    fundingSumDivider: funding.div,
    fundingBlock: funding.feb,
    nextFundingBlock: calculateNextFundingBlock(currentBlock, funding.feb ?? 0, fundingIntervalBlocks),
  };
};

export const updateMarketDataWithFunding = (
  existingMarket: MarketData,
  funding: FundingEvent,
  currentBlock: number
): Partial<MarketData> => {
  const priceDecimals = existingMarket.quoteDecimals ?? DEFAULT_PRICE_DECIMALS;
  return mapFundingData(funding, priceDecimals, currentBlock, existingMarket.fundingIntervalBlocks);
};

export const mapMarkets = (
  markets: Market[],
  exchange: Exchange,
  favoriteMarkets: MarketID[] = []
): Map<number, MarketData> => {
  return markets.reduce((acc: Map<number, MarketData>, market) => {
    const isFavorite = favoriteMarkets.includes(market.id);
    acc.set(market.id, mapMarketData(market, exchange, isFavorite));
    return acc;
  }, new Map<number, MarketData>());
};

export const updateMarketDataWithStats = (existingMarket: MarketData, stats: MarketState): Partial<MarketData> => {
  const priceDecimals = existingMarket.quoteDecimals ?? DEFAULT_PRICE_DECIMALS;
  const quoteDecimals = existingMarket.quote?.decimals ?? DEFAULT_PRICE_DECIMALS;
  const assetDecimals = existingMarket.assetDecimals ?? DEFAULT_TOKEN_DECIMALS;
  const updates: Partial<MarketData> = {};

  if (stats.mrk != null) {
    const newMarkPrice = convertScaledPrice(stats.mrk, priceDecimals) ?? 0;
    const prevMarkPrice = existingMarket.markPrice;

    updates.markPrice = newMarkPrice;
    updates.priceDirection =
      prevMarkPrice == null
        ? 'unchanged'
        : newMarkPrice > prevMarkPrice
          ? 'up'
          : newMarkPrice < prevMarkPrice
            ? 'down'
            : 'unchanged';
  }

  if (stats.orl != null) {
    updates.oraclePrice = convertScaledPrice(stats.orl, priceDecimals) ?? 0;
  }

  if (stats.lst != null) {
    const lastPrice = convertScaledPrice(stats.lst, priceDecimals);
    updates.lastPrice = lastPrice;

    if (stats.prv != null && lastPrice != null) {
      const historicalPrice = convertScaledPrice(stats.prv, priceDecimals) ?? 0;
      const change24h = calculate24hChange(lastPrice, historicalPrice, priceDecimals);
      updates.change24hValue = change24h.changeValue;
      updates.change24hPercentage = change24h.changePercentage;
    }
  }

  if (stats.mid != null) {
    updates.midPrice = convertScaledPrice(stats.mid, priceDecimals);
  }

  if (stats.bid != null) {
    updates.bestBidPrice = convertScaledPrice(stats.bid, priceDecimals);
  }

  if (stats.ask != null) {
    updates.bestAskPrice = convertScaledPrice(stats.ask, priceDecimals);
  }

  if (stats.dva != null) {
    const volumeValue = convertTokenAmount(stats.dva, quoteDecimals);
    updates.volume = formatUsdValue(volumeValue);
    updates.volumeValue = volumeValue;
  }

  if (stats.dv != null) {
    updates.volumeSize = convertScaledPrice(stats.dv, assetDecimals)?.toLocaleString() ?? '';
  }

  // Quote-value OI = size × oracle price; a stats message may carry either input alone.
  if (stats.oi != null || stats.orl != null) {
    const openInterestSizeValue =
      stats.oi != null ? (convertScaledPrice(stats.oi, assetDecimals) ?? 0) : (existingMarket.openInterestSizeValue ?? 0);
    const oraclePrice = updates.oraclePrice ?? existingMarket.oraclePrice ?? 0;
    const openInterestValue = openInterestSizeValue * oraclePrice;

    updates.openInterest = formatUsdValue(openInterestValue);
    updates.openInterestValue = openInterestValue;

    if (stats.oi != null) {
      updates.openInterestSize = openInterestSizeValue.toLocaleString();
      updates.openInterestSizeValue = openInterestSizeValue;
    }
  }

  if (stats.tvl != null) {
    updates.totalCollateralLocked = formatNumberDisplay(convertTokenAmount(stats.tvl, quoteDecimals), {
      maxDecimals: priceDecimals,
    });
  }

  if (stats.at != null) {
    updates.stateTimestamp = stats.at;
  }

  return updates;
};

export const updateMarketDataWithConfig = (existingMarket: MarketData, config: MarketConfig): Partial<MarketData> => {
  const tokenDecimals = existingMarket.quote?.decimals ?? DEFAULT_TOKEN_DECIMALS;

  return {
    isOpen: config.is_open != null ? config.is_open : existingMarket.isOpen,

    maintenanceMarginFraction:
      config.maintenance_margin != null
        ? leverageFromHundredths(config.maintenance_margin)
        : (existingMarket.maintenanceMarginFraction ?? DEFAULT_MAINTENANCE_MARGIN),
    initialMarginFraction:
      config.initial_margin != null
        ? leverageFromHundredths(config.initial_margin)
        : (existingMarket.initialMarginFraction ?? DEFAULT_INITIAL_MARGIN),

    makerFeeMicros: config.maker_fee != null ? convertFeeToBigInt(config.maker_fee) : existingMarket.makerFeeMicros,
    takerFeeMicros: config.taker_fee != null ? convertFeeToBigInt(config.taker_fee) : existingMarket.takerFeeMicros,
    makerFeesMicros: config.maker_fees != null ? config.maker_fees.map((fee) => BigInt(fee)) : existingMarket.makerFeesMicros,
    takerFeesMicros: config.taker_fees != null ? config.taker_fees.map((fee) => BigInt(fee)) : existingMarket.takerFeesMicros,
    contractVersion: config.contract_version ?? existingMarket.contractVersion,

    minPostingAmount:
      config.min_posting_amount != null
        ? convertTokenAmount(config.min_posting_amount, tokenDecimals)
        : existingMarket.minPostingAmount,
    minSettleAmount:
      config.min_settle_amount != null
        ? convertTokenAmount(config.min_settle_amount, tokenDecimals)
        : existingMarket.minSettleAmount,
    recycleFee: config.recycle_fee != null ? convertTokenAmount(config.recycle_fee, tokenDecimals) : existingMarket.recycleFee,

    configTimestamp: config.at,
  };
};
